/**
 * State Management Utilities - Performance Optimized
 * 
 * Provides React-optimized comparison and batching utilities to replace
 * expensive JSON.stringify operations and improve state management performance.
 */

import { useRef, useCallback } from 'react';

/**
 * Shallow comparison utility for React state optimization
 * Replaces expensive JSON.stringify comparisons
 */
export function shallowEqual<T extends Record<string, any>>(prev: T, next: T): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;
  
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  
  if (prevKeys.length !== nextKeys.length) return false;
  
  for (const key of prevKeys) {
    if (prev[key] !== next[key]) return false;
  }
  
  return true;
}

/**
 * Deep comparison for nested objects (optimized for document state)
 * Only compares specific known nested structures to avoid full JSON.stringify
 */
export function deepEqualOptimized<T>(prev: T, next: T): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;
  
  // Handle primitive types
  if (typeof prev !== 'object' || typeof next !== 'object') {
    return prev === next;
  }
  
  // Handle arrays
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) return false;
    return prev.every((item, index) => deepEqualOptimized(item, next[index]));
  }
  
  // Handle objects
  const prevObj = prev as Record<string, any>;
  const nextObj = next as Record<string, any>;
  
  const prevKeys = Object.keys(prevObj);
  const nextKeys = Object.keys(nextObj);
  
  if (prevKeys.length !== nextKeys.length) return false;
  
  for (const key of prevKeys) {
    if (!nextKeys.includes(key)) return false;
    if (!deepEqualOptimized(prevObj[key], nextObj[key])) return false;
  }
  
  return true;
}

/**
 * Batched state update utility
 * Groups related state changes to prevent excessive re-renders
 */
export class StateBatcher<T> {
  private pendingUpdates: Partial<T>[] = [];
  private updateTimer: NodeJS.Timeout | null = null;
  private readonly batchDelay: number;
  private readonly onBatchUpdate: (updates: Partial<T>[]) => void;

  constructor(onBatchUpdate: (updates: Partial<T>[]) => void, batchDelay = 16) {
    this.onBatchUpdate = onBatchUpdate;
    this.batchDelay = batchDelay;
  }

  addUpdate(update: Partial<T>): void {
    this.pendingUpdates.push(update);
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
    
    this.updateTimer = setTimeout(() => {
      this.flush();
    }, this.batchDelay);
  }

  flush(): void {
    if (this.pendingUpdates.length === 0) return;
    
    const updates = [...this.pendingUpdates];
    this.pendingUpdates = [];
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    
    this.onBatchUpdate(updates);
  }

  destroy(): void {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    this.pendingUpdates = [];
  }
}

/**
 * Memoized selector utility (non-hook version)
 * Creates a memoized selector that caches results based on shallow equality
 */
export function createMemoizedSelector<T, R>(
  selector: (state: T) => R
): (state: T) => R {
  let prevState: T | undefined = undefined;
  let prevResult: R | undefined = undefined;
  
  return (state: T): R => {
    if (prevState === undefined || !shallowEqual(prevState as any, state as any)) {
      prevState = state;
      prevResult = selector(state);
    }
    
    return prevResult as R;
  };
}

/**
 * React hook version of memoized selector
 * Use this in React components - must be called at top level
 */
export function useMemoizedSelector<T, R>(
  state: T,
  selector: (state: T) => R,
  deps: React.DependencyList = []
): R {
  const memoizedSelector = useCallback(selector, deps);
  const prevStateRef = useRef<T | undefined>(undefined);
  const prevResultRef = useRef<R | undefined>(undefined);
  
  if (prevStateRef.current === undefined || !shallowEqual(prevStateRef.current as any, state as any)) {
    prevStateRef.current = state;
    prevResultRef.current = memoizedSelector(state);
  }
  
  return prevResultRef.current as R;
}

/**
 * Event subscription manager
 * Consolidates multiple event subscriptions into a single manager
 */
export class EventSubscriptionManager {
  private subscriptions = new Map<string, Set<() => void>>();
  private unsubscribeFunctions = new Map<string, () => void>();

  subscribe(eventType: string, callback: () => void): () => void {
    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, new Set());
    }
    
    this.subscriptions.get(eventType)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.subscriptions.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscriptions.delete(eventType);
          // Clean up engine subscription if no more callbacks
          const unsubscribe = this.unsubscribeFunctions.get(eventType);
          if (unsubscribe) {
            unsubscribe();
            this.unsubscribeFunctions.delete(eventType);
          }
        }
      }
    };
  }

  setEngineUnsubscribe(eventType: string, unsubscribe: () => void): void {
    this.unsubscribeFunctions.set(eventType, unsubscribe);
  }

  notify(eventType: string): void {
    const callbacks = this.subscriptions.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error(`Error in event callback for ${eventType}:`, error);
        }
      });
    }
  }

  destroy(): void {
    // Clean up all engine subscriptions
    this.unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    this.unsubscribeFunctions.clear();
    this.subscriptions.clear();
  }
}

/**
 * Performance monitoring utility
 * Tracks state update performance and identifies bottlenecks
 */
export class StatePerformanceMonitor {
  private updateTimes: number[] = [];
  private readonly maxSamples = 100;

  recordUpdate(startTime: number): void {
    const duration = performance.now() - startTime;
    this.updateTimes.push(duration);
    
    if (this.updateTimes.length > this.maxSamples) {
      this.updateTimes.shift();
    }
    
    // Log slow updates for debugging
    if (duration > 10) {
      console.warn(`[StatePerformanceMonitor] Slow state update: ${duration.toFixed(2)}ms`);
    }
  }

  getMetrics() {
    if (this.updateTimes.length === 0) {
      return { avgUpdateTime: 0, maxUpdateTime: 0, totalUpdates: 0 };
    }
    
    const sum = this.updateTimes.reduce((a, b) => a + b, 0);
    const avg = sum / this.updateTimes.length;
    const max = Math.max(...this.updateTimes);
    
    return {
      avgUpdateTime: avg,
      maxUpdateTime: max,
      totalUpdates: this.updateTimes.length
    };
  }

  reset(): void {
    this.updateTimes = [];
  }
}

/**
 * Error boundary utility for graceful degradation
 */
export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

export function createErrorHandler(
  onError?: (error: Error, errorInfo: string) => void
) {
  return (error: Error, errorInfo: string): ErrorBoundaryState => {
    console.error('[DocumentEngineState] Error caught:', error, errorInfo);
    
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (handlerError) {
        console.error('[DocumentEngineState] Error in error handler:', handlerError);
      }
    }
    
    return {
      hasError: true,
      error,
      errorInfo
    };
  };
}
